// frontend/src/components/FinanceView.js
import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../App';
import { getAllPayouts, reviewPayout, approvePayout, unapprovePayout, exportPayoutsCsv, getUsers, updateUser, getAuditLog, getSyncState, triggerSync, getQuotas, updateQuotas, getApprovers, updateApprovers, getNotificationStatus, sendTestNotification, getPlanAssignments, updatePlanAssignments, getUserPlans, updateUserPlans } from '../api/client';

const fmt = (n) => n == null ? '—' : new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(n);

const fmtDate = (d) => d ? new Date(d).toLocaleString('en-US', {
  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '—';

function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function getCurrentQuarter() {
  const now = new Date();
  const q = Math.ceil((now.getMonth() + 1) / 3);
  return `${now.getFullYear()}-Q${q}`;
}

function generateQuarterOptions() {
  const opts = [];
  const now = new Date();
  // Include one year ahead so finance can set quotas for upcoming quarters.
  for (let y = now.getFullYear() + 1; y >= now.getFullYear() - 1; y--) {
    for (let q = 4; q >= 1; q--) opts.push(`${y}-Q${q}`);
  }
  return opts;
}

const TABS = ['Approval Queue', 'Users', 'Audit Log', 'Sync', 'Quotas'];
// Settings (who can approve commissions) is global-admin only — finance admins
// don't see the tab, and the backend rejects their writes regardless.
const GLOBAL_ADMIN_TABS = ['Settings'];

export default function FinanceView() {
  const { user } = useAuth();
  const [tab, setTab] = useState('Approval Queue');
  const tabs = user?.app_role === 'global_admin' ? [...TABS, ...GLOBAL_ADMIN_TABS] : TABS;

  return (
    <div className="page-enter page-wrap" style={{ maxWidth: '1300px' }}>
      <div style={{ marginBottom: '28px' }}>
        <div className="label" style={{ marginBottom: '6px' }}>Finance & Administration</div>
        <h1 style={{ fontSize: '1.6rem' }}>Finance Panel</h1>
      </div>

      {/* Tabs — horizontal scroll on phones instead of wrapping/overflowing */}
      <div style={{
        display: 'flex', gap: '4px',
        borderBottom: '1px solid var(--border)',
        marginBottom: '28px',
        overflowX: 'auto',
      }}>
        {tabs.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '10px 20px',
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '14px', fontWeight: tab === t ? 600 : 400,
              color: tab === t ? 'var(--text-primary)' : 'var(--text-muted)',
              borderBottom: `2px solid ${tab === t ? 'var(--blue-primary)' : 'transparent'}`,
              marginBottom: '-1px',
              transition: 'all 0.15s',
              fontFamily: 'var(--font-ui)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Approval Queue' && <ApprovalQueue />}
      {tab === 'Users'         && <UsersPanel />}
      {tab === 'Audit Log'     && <AuditLogPanel />}
      {tab === 'Sync'          && <SyncPanel />}
      {tab === 'Quotas'        && <QuotasPanel />}
      {tab === 'Settings'      && <SettingsPanel />}
    </div>
  );
}

// ─── Approval Queue ───────────────────────────────────────────────────────────
// Two-level flow:  pending ──Level 1 review──▶ reviewed ──Final approval──▶ approved
// Which buttons a person sees depends on the approver lists on the Settings tab
// (fetched via getApprovers), not on their app role. The server re-checks every
// action, so the buttons are a convenience, not the security boundary.
function ApprovalQueue() {
  const { user } = useAuth();
  const [month, setMonth] = useState(getCurrentMonth());
  const { data, loading, refetch } = useApi(() => getAllPayouts({ month }), [month]);
  const { data: approvers } = useApi(getApprovers);
  const [busy, setBusy] = useState({});
  const [error, setError] = useState('');

  const payouts  = data?.payouts || [];
  const pending  = payouts.filter(p => p.status === 'pending');
  const reviewed = payouts.filter(p => p.status === 'reviewed');
  const done     = payouts.filter(p => !['pending', 'reviewed'].includes(p.status));

  const isGlobalAdmin = user?.app_role === 'global_admin';
  const me = approvers?.me || { level1: false, level2: false };
  const noApproversConfigured = approvers && approvers.level1.length === 0 && approvers.level2.length === 0;

  // One handler for all three actions — same busy-key + refetch + error surfacing.
  const act = async (fn, repId, pMonth) => {
    const key = `${repId}-${pMonth}`;
    setBusy(b => ({ ...b, [key]: true }));
    setError('');
    try {
      await fn(repId, pMonth);
      await refetch();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(b => ({ ...b, [key]: false }));
    }
  };

  const handleReview  = (repId, pMonth) => act(reviewPayout, repId, pMonth);
  const handleApprove = (repId, pMonth) => act(approvePayout, repId, pMonth);
  const handleSendBack = (repId, pMonth, repName, status) => {
    const ok = window.confirm(
      status === 'approved'
        ? `Un-approve ${repName}'s ${pMonth} payout?\n\nIt returns to Pending, loses both sign-offs, ` +
          `and will be recalculated on the next sync. This action is audit-logged.`
        : `Send ${repName}'s ${pMonth} payout back?\n\nIt returns to Pending for a fresh Level 1 review ` +
          `and will be recalculated on the next sync. This action is audit-logged.`
    );
    if (ok) act(unapprovePayout, repId, pMonth);
  };

  const sum = (rows) => rows.reduce((s, p) => s + (parseFloat(p.total_payout) || 0), 0);

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap' }}>
        <input
          type="month"
          value={month}
          onChange={e => setMonth(e.target.value)}
          className="input"
          style={{ width: 'auto' }}
        />
        <button onClick={() => exportPayoutsCsv({ month })} className="btn btn-ghost">
          ↓ Export CSV
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {pending.length > 0 && (
            <QueuePill color="var(--amber)" bg="var(--amber-subtle)">
              {pending.length} awaiting Level 1 · {fmt(sum(pending))}
            </QueuePill>
          )}
          {reviewed.length > 0 && (
            <QueuePill color="var(--blue-sky)" bg="rgba(57, 185, 223, 0.14)">
              {reviewed.length} awaiting final · {fmt(sum(reviewed))}
            </QueuePill>
          )}
        </div>
      </div>

      {/* Where the signed-in person sits in the chain */}
      {approvers && (
        <div style={{
          marginBottom: '20px', padding: '10px 14px',
          background: 'var(--bg-elevated)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', fontSize: '13px', color: 'var(--text-secondary)',
          display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center',
        }}>
          <span>
            <strong style={{ color: 'var(--text-primary)' }}>Level 1 review:</strong>{' '}
            {approvers.level1.length ? approvers.level1.map(u => u.display_name).join(', ') : <em>nobody assigned</em>}
          </span>
          <span>
            <strong style={{ color: 'var(--text-primary)' }}>Final approval:</strong>{' '}
            {approvers.level2.length ? approvers.level2.map(u => u.display_name).join(', ') : <em>nobody assigned</em>}
          </span>
          <span style={{ marginLeft: 'auto', color: 'var(--text-muted)' }}>
            {me.level1 && me.level2 ? 'You can review and give final approval'
              : me.level1 ? 'You can do Level 1 reviews'
              : me.level2 ? 'You give final approval'
              : 'You can view the queue but are not an approver'}
          </span>
        </div>
      )}

      {noApproversConfigured && (
        <div style={{
          marginBottom: '20px', padding: '12px 16px',
          background: 'var(--amber-subtle)', borderLeft: '3px solid var(--amber)',
          borderRadius: 'var(--radius-sm)', fontSize: '13px', color: 'var(--text-secondary)',
        }}>
          No approvers are configured, so nothing can be approved yet.
          {isGlobalAdmin ? ' Assign them on the Settings tab.' : ' Ask a global admin to assign them on the Settings tab.'}
        </div>
      )}

      {error && (
        <div style={{
          marginBottom: '20px', padding: '12px 16px',
          background: 'var(--red-subtle)', borderLeft: '3px solid var(--red)',
          borderRadius: 'var(--radius-sm)', fontSize: '13px', color: 'var(--text-secondary)',
        }}>
          {error}
        </div>
      )}

      {/* Step 1 — pending */}
      {pending.length > 0 && (
        <div className="card" style={{ overflow: 'hidden', marginBottom: '24px' }}>
          <QueueHeader dot="var(--amber)" title={`Awaiting Level 1 review (${pending.length})`}
            sub="First sign-off. Reviewing locks the figures so they stop recalculating." />
          <div style={{ overflowX: 'auto' }}>
            <PayoutApprovalTable
              payouts={pending}
              busy={busy}
              renderActions={(p) => me.level1 ? (
                <button onClick={() => handleReview(p.rep_id, p.payout_month)} disabled={busy[`${p.rep_id}-${p.payout_month}`]} className="btn btn-primary btn-sm">
                  {busy[`${p.rep_id}-${p.payout_month}`] ? '...' : 'Review ✓'}
                </button>
              ) : <Muted>Awaiting Level 1</Muted>}
            />
          </div>
        </div>
      )}

      {/* Step 2 — reviewed */}
      {reviewed.length > 0 && (
        <div className="card" style={{ overflow: 'hidden', marginBottom: '24px' }}>
          <QueueHeader dot="var(--blue-sky)" title={`Awaiting final approval (${reviewed.length})`}
            sub="Second sign-off. Must be a different person than the Level 1 reviewer." />
          <div style={{ overflowX: 'auto' }}>
            <PayoutApprovalTable
              payouts={reviewed}
              busy={busy}
              showReviewer
              renderActions={(p) => {
                const key = `${p.rep_id}-${p.payout_month}`;
                const iReviewedIt = Number(p.reviewed_by_user_id) === Number(user?.id);
                const canSendBack = me.level2 || isGlobalAdmin;
                return (
                  <span style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
                    {me.level2 && !iReviewedIt && (
                      <button onClick={() => handleApprove(p.rep_id, p.payout_month)} disabled={busy[key]} className="btn btn-primary btn-sm">
                        {busy[key] ? '...' : 'Approve'}
                      </button>
                    )}
                    {me.level2 && iReviewedIt && <Muted>You reviewed this</Muted>}
                    {!me.level2 && <Muted>Awaiting final</Muted>}
                    {canSendBack && (
                      <button onClick={() => handleSendBack(p.rep_id, p.payout_month, p.rep_name, p.status)} disabled={busy[key]} className="btn btn-ghost btn-sm">
                        Send back
                      </button>
                    )}
                  </span>
                );
              }}
            />
          </div>
        </div>
      )}

      {/* Approved / paid */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <QueueHeader title={`Approved / Paid (${done.length})`} />
        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <div className="loader-ring" style={{ margin: '0 auto' }} />
          </div>
        ) : done.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '14px' }}>
            No approved payouts for {month}.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <PayoutApprovalTable
              payouts={done}
              busy={busy}
              showReviewer
              showApprover
              renderActions={isGlobalAdmin ? (p) => p.status === 'approved' ? (
                <button onClick={() => handleSendBack(p.rep_id, p.payout_month, p.rep_name, p.status)} disabled={busy[`${p.rep_id}-${p.payout_month}`]} className="btn btn-ghost btn-sm">
                  {busy[`${p.rep_id}-${p.payout_month}`] ? '...' : 'Un-approve'}
                </button>
              ) : null : null}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function QueuePill({ color, bg, children }) {
  return (
    <div style={{
      padding: '8px 16px', background: bg, border: `1px solid ${color}`,
      borderRadius: 'var(--radius-sm)', fontSize: '13px', color, fontWeight: 600,
    }}>
      {children}
    </div>
  );
}

function QueueHeader({ dot, title, sub }) {
  return (
    <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
      {dot && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: dot }} />}
      <h3>{title}</h3>
      {sub && <span style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>{sub}</span>}
    </div>
  );
}

function Muted({ children }) {
  return <span style={{ fontSize: '12px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{children}</span>;
}

const fmtSignoff = (name, at) => name
  ? <span title={at ? new Date(at).toLocaleString() : ''}>{name}</span>
  : <span style={{ color: 'var(--text-muted)' }}>—</span>;

// `renderActions(p)` returns the action cell for a row (or null for no column).
function PayoutApprovalTable({ payouts, renderActions = null, showReviewer = false, showApprover = false }) {
  const hasActionColumn = typeof renderActions === 'function';
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>Rep</th>
          <th style={{ textAlign: 'right' }}>HW/SW</th>
          <th style={{ textAlign: 'right' }}>Services</th>
          <th style={{ textAlign: 'right' }}>FOD</th>
          <th style={{ textAlign: 'right' }}>Total</th>
          <th>Pay Date</th>
          <th>Status</th>
          {showReviewer && <th>Reviewed by</th>}
          {showApprover && <th>Approved by</th>}
          {hasActionColumn && <th />}
        </tr>
      </thead>
      <tbody>
        {payouts.map(p => (
          <tr key={`${p.rep_id}-${p.payout_month}`}>
            <td style={{ fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
              {p.rep_name}
              {p.plan_mix && p.plan_mix !== 'rep' && (
                <span title={`Managed Services plan portion: ${fmt(p.ms_total_payout)}`} style={{
                  marginLeft: '8px', fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
                  padding: '1px 6px', borderRadius: '8px', verticalAlign: 'middle',
                  background: p.plan_mix === 'msa' ? 'rgba(57, 185, 223, 0.14)' : 'var(--amber-subtle)',
                  color: p.plan_mix === 'msa' ? 'var(--blue-sky)' : 'var(--amber)',
                }}>
                  {p.plan_mix === 'msa' ? 'MS plan' : 'Mixed'}
                </span>
              )}
            </td>
            {/* AE-plan components; the MS-plan portion is inside Total (hover the badge) */}
            <td className="td-number">{fmt(p.hw_sw_payout)}</td>
            <td className="td-number">{fmt(p.services_payout)}</td>
            <td className="td-number">{fmt(p.fod_payout)}</td>
            <td className="td-number" style={{ color: 'var(--green)', fontWeight: 600 }}>{fmt(p.total_payout)}</td>
            <td style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
              {p.pay_date ? new Date(p.pay_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '—'}
            </td>
            <td><span className={`badge badge-${p.status}`}>{p.status}</span></td>
            {showReviewer && <td style={{ fontSize: '13px' }}>{fmtSignoff(p.reviewed_by, p.reviewed_at)}</td>}
            {showApprover && <td style={{ fontSize: '13px' }}>{fmtSignoff(p.approved_by, p.approved_at)}</td>}
            {hasActionColumn && <td>{renderActions(p)}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Users Panel ──────────────────────────────────────────────────────────────
function UsersPanel() {
  const { data, loading, refetch } = useApi(getUsers);
  const { data: plansData, refetch: refetchPlans } = useApi(getPlanAssignments);
  const { data: userPlansData, refetch: refetchUserPlans } = useApi(getUserPlans);
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState({});
  const [planEditor, setPlanEditor] = useState(null);   // users.id whose plan editor is open

  const users = data?.users || [];
  const departments = data?.departments?.length ? data.departments : [
    { code: 'sales', label: 'Sales' }, { code: 'services', label: 'Services' }, { code: 'operations', label: 'Operations' },
    { code: 'presales', label: 'Pre-Sales' }, { code: 'finance', label: 'Finance' },
  ];
  const plansByRep = plansData?.byRep || {};
  const planLabels = plansData?.labels || { rep: 'AE plan', msa: 'Managed Services plan' };
  const userPlansByUser = userPlansData?.byUser || {};
  const userPlanLabels = userPlansData?.labels || { none: 'No leadership plan', sales_manager: 'Sales manager comp', ops_director: 'Director of Operations plan' };

  // Current leadership plan for a user = latest assignment on/before today; none → 'none'.
  const currentUserPlanFor = (userId) => {
    const rows = userPlansByUser[userId] || [];
    const today = new Date().toISOString().slice(0, 10);
    const live = rows.filter(a => a.effective_from <= today).sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0];
    const future = rows.filter(a => a.effective_from > today).sort((a, b) => a.effective_from.localeCompare(b.effective_from))[0];
    return { code: live?.plan_code || 'none', since: live?.effective_from || null, next: future || null };
  };

  // Current plan for a rep = latest assignment on/before today; none → AE plan.
  const currentPlanFor = (repId) => {
    const rows = plansByRep[repId] || [];
    const today = new Date().toISOString().slice(0, 10);
    const live = rows.filter(a => a.effective_from <= today).sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0];
    const future = rows.filter(a => a.effective_from > today).sort((a, b) => a.effective_from.localeCompare(b.effective_from))[0];
    return { code: live?.plan_code || 'rep', since: live?.effective_from || null, next: future || null };
  };

  const handleChange = (id, field, value) => {
    setEditing(e => ({ ...e, [id]: { ...(e[id] || {}), [field]: value } }));
  };

  const handleSave = async (id) => {
    setSaving(s => ({ ...s, [id]: true }));
    try {
      await updateUser(id, editing[id]);
      setEditing(e => { const n = { ...e }; delete n[id]; return n; });
      await refetch();
    } finally {
      setSaving(s => ({ ...s, [id]: false }));
    }
  };

  if (loading) return <div style={{ padding: '48px', textAlign: 'center' }}><div className="loader-ring" style={{ margin: '0 auto' }} /></div>;

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)' }}>
        <h3>User Management ({users.length})</h3>
        <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>
          Assign roles and departments, link NetSuite rep IDs, and set each person's comp plans: the AE/Managed
          Services plan that pays a rep's deals (by close date) and any leadership plan they accrue (Sales manager comp,
          Director of Operations).
          Users are created automatically on first login. Only the Operations and Finance departments can view every other department.
        </p>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Department</th>
              <th>NS Rep ID</th>
              <th>Comp Plan</th>
              <th>Active</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const edits = editing[u.id] || {};
              const isDirty = Object.keys(edits).length > 0;
              const repId = u.ns_rep_id;
              const cur = repId ? currentPlanFor(repId) : null;
              const lead = currentUserPlanFor(u.id);
              const open = planEditor === u.id;
              return (
                <React.Fragment key={u.id}>
                <tr>
                  <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{u.display_name}</td>
                  <td style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{u.email}</td>
                  <td>
                    <select
                      value={edits.app_role ?? u.app_role}
                      onChange={e => handleChange(u.id, 'app_role', e.target.value)}
                      className="input"
                      style={{ width: 'auto', padding: '4px 8px', fontSize: '13px' }}
                    >
                      <option value="rep">Rep</option>
                      <option value="manager">Manager</option>
                      <option value="finance_admin">Finance Admin</option>
                      <option value="global_admin">Global Admin</option>
                    </select>
                  </td>
                  <td>
                    <select
                      value={edits.department ?? (u.department || 'sales')}
                      onChange={e => handleChange(u.id, 'department', e.target.value)}
                      className="input"
                      style={{ width: 'auto', padding: '4px 8px', fontSize: '13px' }}
                      title="Which department's menu this user sees. Operations and Finance can view all departments."
                    >
                      {departments.map(d => <option key={d.code} value={d.code}>{d.label}</option>)}
                    </select>
                  </td>
                  <td>
                    <input
                      value={edits.ns_rep_id ?? (u.ns_rep_id || '')}
                      onChange={e => handleChange(u.id, 'ns_rep_id', e.target.value)}
                      placeholder="NS Employee ID"
                      className="input"
                      style={{ width: '140px', padding: '4px 8px', fontSize: '13px' }}
                    />
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => setPlanEditor(open ? null : u.id)}
                      className="btn btn-ghost btn-sm"
                      title="Edit this person's comp plans"
                      style={{ padding: '4px 10px', fontSize: '12.5px', color: 'var(--text-secondary)', borderColor: open ? 'var(--border-bright)' : undefined, textAlign: 'left', whiteSpace: 'nowrap', lineHeight: 1.45, display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                    >
                      <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      {repId && (
                        <span style={{ color: cur.code === 'msa' ? 'var(--blue-sky)' : 'var(--text-primary)' }}>
                          {planLabels[cur.code] || cur.code}
                          {cur.next && <span style={{ color: 'var(--amber)', fontWeight: 400 }}> → {planLabels[cur.next.plan_code]} {cur.next.effective_from}</span>}
                        </span>
                      )}
                      {lead.code !== 'none' && (
                        <span style={{ color: 'var(--amber)' }}>
                          {userPlanLabels[lead.code]}
                          {lead.next && <span style={{ fontWeight: 400 }}> → {userPlanLabels[lead.next.plan_code]} {lead.next.effective_from}</span>}
                        </span>
                      )}
                      {!repId && lead.code === 'none' && <span style={{ color: 'var(--text-muted)' }}>No plan{lead.next ? ` → ${userPlanLabels[lead.next.plan_code]} ${lead.next.effective_from}` : ''}</span>}
                      </span>
                      <span style={{ color: 'var(--text-muted)' }}>{open ? '▾' : '▸'}</span>
                    </button>
                  </td>
                  <td>
                    <select
                      value={String(edits.active ?? u.active)}
                      onChange={e => handleChange(u.id, 'active', e.target.value === 'true')}
                      className="input"
                      style={{ width: 'auto', padding: '4px 8px', fontSize: '13px' }}
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </td>
                  <td>
                    {isDirty && (
                      <button
                        onClick={() => handleSave(u.id)}
                        disabled={saving[u.id]}
                        className="btn btn-primary btn-sm"
                      >
                        {saving[u.id] ? '...' : 'Save'}
                      </button>
                    )}
                  </td>
                </tr>
                {open && (
                  <tr>
                    <td colSpan={8} style={{ padding: 0, background: 'var(--bg-elevated)' }}>
                      {repId ? (
                        <PlanHistoryEditor
                          repId={repId}
                          repName={u.display_name}
                          rows={plansByRep[repId] || []}
                          labels={planLabels}
                          onSaved={async () => { await refetchPlans(); }}
                          onClose={() => setPlanEditor(null)}
                        />
                      ) : (
                        <div style={{ padding: '12px 24px 0', fontSize: '12.5px', color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                          <span>{u.display_name} has no NetSuite rep ID, so no AE / Managed Services plan applies. Link one above if they also sell.</span>
                          <button onClick={() => setPlanEditor(null)} className="btn btn-ghost btn-sm">✕ Close</button>
                        </div>
                      )}
                      <UserPlanEditor
                        userId={u.id}
                        userName={u.display_name}
                        rows={userPlansByUser[u.id] || []}
                        labels={userPlanLabels}
                        onSaved={async () => { await refetchUserPlans(); }}
                      />
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Comp plan history editor (inline under a user row) ───────────────────────
// A rep's plan is a list of dated changes. Each row means "deals CLOSED on or
// after this date pay under this plan"; anything closed before the earliest row
// is on the AE plan. Payout month is still the ship month — the close date only
// picks the rules. Saved as a whole; takes effect on the next sync/recalc, and
// never re-rates months that are already reviewed or approved.
function PlanHistoryEditor({ repId, repName, rows, labels, onSaved, onClose }) {
  const [draft, setDraft] = useState(() => rows.map(r => ({ plan_code: r.plan_code, effective_from: r.effective_from, note: r.note || '' })));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const sorted = [...draft].sort((a, b) => (a.effective_from || '9999').localeCompare(b.effective_from || '9999'));
  const setRow = (i, patch) => setDraft(d => d.map((r, j) => j === i ? { ...r, ...patch } : r));
  const addRow = () => setDraft(d => [...d, { plan_code: 'msa', effective_from: '', note: '' }]);
  const removeRow = (i) => setDraft(d => d.filter((_, j) => j !== i));

  const problems = [];
  const dates = draft.map(r => r.effective_from);
  if (dates.some(d => !/^\d{4}-\d{2}-\d{2}$/.test(d))) problems.push('Every change needs an effective date.');
  if (new Set(dates).size !== dates.length) problems.push('Two changes have the same date.');

  const handleSave = async () => {
    setSaving(true); setMsg(null);
    try {
      await updatePlanAssignments(repId, draft.map(r => ({ plan_code: r.plan_code, effective_from: r.effective_from, note: r.note || null })));
      await onSaved();
      setMsg({ tone: 'ok', text: 'Saved. Applies on the next sync and recalc; reviewed/approved months are not changed.' });
    } catch (err) {
      setMsg({ tone: 'err', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '…';

  return (
    <div style={{ padding: '16px 24px 18px', borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <div>
          <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text-primary)' }}>{repName} — comp plan history</div>
          <div style={{ fontSize: '12.5px', color: 'var(--text-muted)', marginTop: '3px', lineHeight: 1.5, maxWidth: '720px' }}>
            Each row says: deals <strong>closed on or after</strong> this date pay under this plan. Anything closed earlier
            stays on the AE plan. When a deal ships, the app looks at its close date to pick the rules — so
            "Managed Services plan from Oct 1" means a deal closed Sep 30 still pays the AE way when it ships in November.
          </div>
        </div>
        <button onClick={onClose} className="btn btn-ghost btn-sm">✕ Close</button>
      </div>

      {/* Timeline summary */}
      <div style={{ fontSize: '12.5px', color: 'var(--text-secondary)', marginBottom: '12px', fontFamily: 'var(--font-data)' }}>
        <span style={{ color: 'var(--text-primary)' }}>AE plan</span>
        {sorted.map((r, i) => (
          <span key={i}> → <span style={{ color: r.plan_code === 'msa' ? 'var(--blue-sky)' : 'var(--text-primary)' }}>{labels[r.plan_code]}</span> from {fmtD(r.effective_from)}</span>
        ))}
        {sorted.length === 0 && <span style={{ color: 'var(--text-muted)' }}> (no changes — AE plan for everything)</span>}
      </div>

      {draft.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
          {draft.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)', width: '110px' }}>Deals closed on/after</span>
              <input type="date" className="input" value={r.effective_from} onChange={e => setRow(i, { effective_from: e.target.value })} style={{ width: '160px', padding: '5px 8px', fontSize: '13px' }} />
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>pay under</span>
              <select className="input" value={r.plan_code} onChange={e => setRow(i, { plan_code: e.target.value })} style={{ width: 'auto', padding: '5px 8px', fontSize: '13px' }}>
                {Object.entries(labels).map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </select>
              <input className="input" placeholder="Note (optional)" value={r.note} onChange={e => setRow(i, { note: e.target.value })} style={{ flex: '1 1 180px', minWidth: '160px', padding: '5px 8px', fontSize: '13px' }} />
              <button onClick={() => removeRow(i)} className="btn btn-ghost btn-sm" aria-label="Remove" style={{ color: 'var(--text-muted)' }}>✕</button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <button onClick={addRow} className="btn btn-ghost btn-sm">+ Add a plan change</button>
        <button onClick={handleSave} disabled={saving || problems.length > 0} className="btn btn-primary btn-sm">{saving ? 'Saving…' : 'Save plan history'}</button>
        {problems.length > 0 && <span style={{ fontSize: '12.5px', color: 'var(--amber)' }}>{problems.join(' ')}</span>}
        {msg && <span style={{ fontSize: '12.5px', color: msg.tone === 'ok' ? 'var(--green)' : 'var(--red)' }}>{msg.text}</span>}
      </div>
    </div>
  );
}

// Leadership plan history for one person (Sales manager comp / Director of
// Operations / none). Same dated-row model as the rep plan editor, but keyed by
// the user, so it follows them through role and department changes.
function UserPlanEditor({ userId, userName, rows, labels, onSaved }) {
  const [draft, setDraft] = useState(() => rows.map(r => ({ plan_code: r.plan_code, effective_from: r.effective_from, note: r.note || '' })));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => { setDraft(rows.map(r => ({ plan_code: r.plan_code, effective_from: r.effective_from, note: r.note || '' }))); }, [rows]);

  const sorted = [...draft].sort((a, b) => (a.effective_from || '9999').localeCompare(b.effective_from || '9999'));
  const setRow = (i, patch) => setDraft(d => d.map((r, j) => j === i ? { ...r, ...patch } : r));
  const quarterStart = () => { const n = new Date(); return `${n.getUTCFullYear()}-${String(Math.floor(n.getUTCMonth() / 3) * 3 + 1).padStart(2, '0')}-01`; };
  const addRow = () => setDraft(d => [...d, { plan_code: 'sales_manager', effective_from: quarterStart(), note: '' }]);
  const removeRow = (i) => setDraft(d => d.filter((_, j) => j !== i));

  const problems = [];
  const dates = draft.map(r => r.effective_from);
  if (dates.some(d => !/^\d{4}-\d{2}-\d{2}$/.test(d))) problems.push('Every change needs an effective date.');
  if (new Set(dates).size !== dates.length) problems.push('Two changes have the same date.');

  const handleSave = async () => {
    setSaving(true); setMsg(null);
    try {
      await updateUserPlans(userId, draft.map(r => ({ plan_code: r.plan_code, effective_from: r.effective_from, note: r.note || null })));
      await onSaved();
      setMsg({ tone: 'ok', text: 'Saved. The Finance roll-up uses this immediately.' });
    } catch (err) {
      setMsg({ tone: 'err', text: err.message });
    } finally {
      setSaving(false);
    }
  };
  const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '…';
  const color = (code) => code === 'none' ? 'var(--text-muted)' : 'var(--amber)';

  return (
    <div style={{ padding: '14px 24px 18px', borderTop: '1px solid var(--border)' }}>
      <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text-primary)' }}>{userName} — leadership plan</div>
      <div style={{ fontSize: '12.5px', color: 'var(--text-muted)', marginTop: '3px', lineHeight: 1.5, maxWidth: '720px', marginBottom: '10px' }}>
        Quarterly plans that belong to the person, not to their deals: <strong>Sales manager comp</strong> (paid on bookings) and the
        <strong> Director of Operations plan</strong>. Each row says: from this date, this person is on this plan. Use
        "No leadership plan" to end one. This decides who accrues on the Finance dashboard, regardless of role.
      </div>
      <div style={{ fontSize: '12.5px', color: 'var(--text-secondary)', marginBottom: '12px', fontFamily: 'var(--font-data)' }}>
        <span style={{ color: 'var(--text-muted)' }}>No leadership plan</span>
        {sorted.map((r, i) => <span key={i}> → <span style={{ color: color(r.plan_code) }}>{labels[r.plan_code]}</span> from {fmtD(r.effective_from)}</span>)}
      </div>
      {draft.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
          {draft.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)', width: '110px' }}>From</span>
              <input type="date" className="input" value={r.effective_from} onChange={e => setRow(i, { effective_from: e.target.value })} style={{ width: '160px', padding: '5px 8px', fontSize: '13px' }} />
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>on</span>
              <select className="input" value={r.plan_code} onChange={e => setRow(i, { plan_code: e.target.value })} style={{ width: 'auto', padding: '5px 8px', fontSize: '13px' }}>
                {Object.entries(labels).map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </select>
              <input className="input" placeholder="Note (optional)" value={r.note} onChange={e => setRow(i, { note: e.target.value })} style={{ flex: '1 1 180px', minWidth: '160px', padding: '5px 8px', fontSize: '13px' }} />
              <button onClick={() => removeRow(i)} className="btn btn-ghost btn-sm" aria-label="Remove" style={{ color: 'var(--text-muted)' }}>✕</button>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <button onClick={addRow} className="btn btn-ghost btn-sm">+ Add a plan change</button>
        <button onClick={handleSave} disabled={saving || problems.length > 0} className="btn btn-primary btn-sm">{saving ? 'Saving…' : 'Save leadership plan'}</button>
        {problems.length > 0 && <span style={{ fontSize: '12.5px', color: 'var(--amber)' }}>{problems.join(' ')}</span>}
        {msg && <span style={{ fontSize: '12.5px', color: msg.tone === 'ok' ? 'var(--green)' : 'var(--red)' }}>{msg.text}</span>}
      </div>
    </div>
  );
}

// ─── Audit Log ────────────────────────────────────────────────────────────────
function AuditLogPanel() {
  const { data, loading } = useApi(getAuditLog);
  const entries = data?.entries || [];

  const eventColor = {
    sync_complete:     'var(--blue-primary)',
    calc_run:          'var(--text-accent)',
    payout_reviewed:   'var(--blue-sky)',
    payout_approved:   'var(--green)',
    payout_unapproved: 'var(--amber)',
    approvers_updated: 'var(--blue-primary)',
    plan_assignments_updated: 'var(--blue-sky)',
    user_plan_assignments_updated: 'var(--amber)',
    ops_plan_settings_updated: 'var(--amber)',
    ops_rock_added: 'var(--blue-sky)', ops_rock_updated: 'var(--blue-sky)', ops_rock_deleted: 'var(--amber)',
  };

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)' }}>
        <h3>Audit Log</h3>
        <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>
          Full history of syncs, calculations, and approvals.
        </p>
      </div>
      {loading ? (
        <div style={{ padding: '48px', textAlign: 'center' }}><div className="loader-ring" style={{ margin: '0 auto' }} /></div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Event</th>
                <th>Actor</th>
                <th>Rep</th>
                <th>Month</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id}>
                  <td style={{ fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'var(--font-data)' }}>
                    {fmtDate(e.created_at)}
                  </td>
                  <td>
                    <span style={{
                      fontSize: '11px', fontWeight: 600, letterSpacing: '0.05em',
                      color: eventColor[e.event_type] || 'var(--text-muted)',
                      textTransform: 'uppercase',
                    }}>
                      {e.event_type.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td style={{ fontSize: '13px' }}>{e.actor_name || '—'}</td>
                  <td style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{e.rep_id || '—'}</td>
                  <td style={{ fontSize: '13px', fontFamily: 'var(--font-data)' }}>{e.payout_month || '—'}</td>
                  <td style={{ fontSize: '12px', color: 'var(--text-muted)', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {e.details ? JSON.stringify(e.details) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Sync Panel ───────────────────────────────────────────────────────────────
function SyncPanel() {
  const { data, loading, refetch } = useApi(getSyncState);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });

  const syncStates = data?.syncState || [];

  const handleSync = async () => {
    setSyncing(true);
    setSyncMsg('');
    try {
      const res = await triggerSync(dateRange);
      setSyncMsg(res?.message || 'Sync triggered successfully.');
      setTimeout(refetch, 2000);
    } catch (err) {
      setSyncMsg(`Error: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

      {/* Sync status cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
        {loading ? (
          [1,2].map(i => <div key={i} className="skeleton" style={{ height: '100px', borderRadius: '16px' }} />)
        ) : syncStates.map(s => (
          <div key={s.sync_type} className="card" style={{ padding: '20px 24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div className="label" style={{ marginBottom: '6px' }}>
                  {s.sync_type.replace(/_/g, ' ')}
                </div>
                <div style={{
                  fontSize: '13px', fontWeight: 600,
                  color: s.status === 'complete' ? 'var(--green)' : s.status === 'error' ? 'var(--red)' : 'var(--amber)',
                  textTransform: 'capitalize',
                }}>
                  {s.status}
                </div>
              </div>
              {s.last_synced_at && (
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'right' }}>
                  Last sync<br />
                  <span style={{ fontFamily: 'var(--font-data)' }}>
                    {fmtDate(s.last_synced_at)}
                  </span>
                </div>
              )}
            </div>
            {s.error_message && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--red)', background: 'var(--red-subtle)', padding: '8px', borderRadius: 'var(--radius-sm)' }}>
                {s.error_message}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Manual sync trigger */}
      <div className="card" style={{ padding: '24px' }}>
        <h3 style={{ marginBottom: '8px' }}>Manual Sync</h3>
        <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' }}>
          Override the nightly sync to pull a specific date range from NetSuite.
          Leave blank to sync from current quarter start through today.
        </p>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <div className="label" style={{ marginBottom: '6px' }}>Start Date</div>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={e => setDateRange(d => ({ ...d, startDate: e.target.value }))}
              className="input"
              style={{ width: '160px' }}
            />
          </div>
          <div>
            <div className="label" style={{ marginBottom: '6px' }}>End Date</div>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={e => setDateRange(d => ({ ...d, endDate: e.target.value }))}
              className="input"
              style={{ width: '160px' }}
            />
          </div>
          <button
            onClick={handleSync}
            disabled={syncing}
            className="btn btn-primary"
          >
            {syncing ? 'Triggering...' : '⟳ Run Sync Now'}
          </button>
        </div>
        {syncMsg && (
          <div style={{
            marginTop: '16px', padding: '12px 16px',
            background: 'var(--blue-subtle)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)', fontSize: '13px', color: 'var(--text-accent)',
          }}>
            {syncMsg}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Settings Panel (global admins only) ──────────────────────────────────────
// Who signs off on commissions, and at which level. Saved as a whole (both lists
// together) so the chain can never be half-updated.
const FINANCE_ROLES = new Set(['finance_admin', 'global_admin']);

function SettingsPanel() {
  const { user } = useAuth();
  const { data: usersData, loading: usersLoading } = useApi(getUsers);
  const { data: approvers, loading: apprLoading, refetch } = useApi(getApprovers);
  const [edits, setEdits]   = useState(null);   // { level1:Set, level2:Set } once the user touches anything
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState(null);   // { tone: 'ok'|'err', text }

  // Active users, with the people who can actually reach Finance listed first —
  // approvers are almost always finance/admin, so reps shouldn't bury them.
  const ROLE_ORDER = { global_admin: 0, finance_admin: 1, manager: 2, rep: 3 };
  const users = (usersData?.users || [])
    .filter(u => u.active)
    .sort((a, b) => (ROLE_ORDER[a.app_role] ?? 9) - (ROLE_ORDER[b.app_role] ?? 9)
      || (a.display_name || '').localeCompare(b.display_name || ''));

  // Working copy: unsaved edits if any, else what the server has.
  const current = edits || {
    level1: new Set((approvers?.level1 || []).map(u => u.id)),
    level2: new Set((approvers?.level2 || []).map(u => u.id)),
  };
  const isDirty = edits != null;

  const toggle = (level, id) => {
    setMsg(null);
    setEdits(prev => {
      const base = prev || current;
      const next = { level1: new Set(base.level1), level2: new Set(base.level2) };
      next[level].has(id) ? next[level].delete(id) : next[level].add(id);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await updateApprovers({ level1: [...current.level1], level2: [...current.level2] });
      setEdits(null);
      await refetch();
      setMsg({ tone: 'ok', text: 'Saved. The approval queue now uses these approvers.' });
    } catch (err) {
      setMsg({ tone: 'err', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const discard = () => { setEdits(null); setMsg(null); };

  if (usersLoading || apprLoading) {
    return <div style={{ padding: '48px', textAlign: 'center' }}><div className="loader-ring" style={{ margin: '0 auto' }} /></div>;
  }

  // People who would be picked but can't reach the Finance area yet.
  const needsRole = users.filter(u => (current.level1.has(u.id) || current.level2.has(u.id)) && !FINANCE_ROLES.has(u.app_role));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '960px' }}>
      <div className="card" style={{ padding: '24px' }}>
        <h3 style={{ marginBottom: '8px' }}>Commission Approvers</h3>
        <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0, lineHeight: 1.6 }}>
          Every monthly payout needs two sign-offs before it can be exported to payroll:
          a <strong style={{ color: 'var(--text-primary)' }}>Level 1 review</strong>, then a{' '}
          <strong style={{ color: 'var(--text-primary)' }}>final approval</strong> from a different person.
          Once reviewed, the figures are locked and stop recalculating on sync. Approval authority comes
          only from these lists — a Finance Admin or Global Admin who isn't listed can see the queue but
          can't sign anything off. Only people who have signed in at least once appear here.
        </p>

        {/* Two-column picker */}
        <div style={{ display: 'grid', gap: '20px', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', marginTop: '22px' }}>
          <ApproverColumn
            step="1"
            title="Level 1 — Review"
            blurb="First look at the numbers. Reviewing moves a payout to “reviewed” and freezes it."
            color="var(--amber)"
            users={users}
            selected={current.level1}
            onToggle={(id) => toggle('level1', id)}
          />
          <ApproverColumn
            step="2"
            title="Level 2 — Final approval"
            blurb="Signs off on what Level 1 reviewed. Can also send a payout back for another look."
            color="var(--blue-primary)"
            users={users}
            selected={current.level2}
            onToggle={(id) => toggle('level2', id)}
          />
        </div>

        {needsRole.length > 0 && (
          <div style={{
            marginTop: '18px', padding: '12px 16px',
            background: 'var(--amber-subtle)', borderLeft: '3px solid var(--amber)',
            borderRadius: 'var(--radius-sm)', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6,
          }}>
            <strong style={{ color: 'var(--text-primary)' }}>Heads up:</strong>{' '}
            {needsRole.map(u => `${u.display_name} (${ROLE_LABEL[u.app_role] || u.app_role})`).join(', ')}{' '}
            {needsRole.length === 1 ? 'doesn’t' : 'don’t'} have the Finance Admin role, so{' '}
            {needsRole.length === 1 ? 'they' : 'they'} can’t open the Finance area to approve anything.
            Change the role on the Users tab.
          </div>
        )}

        {(current.level1.size === 0 || current.level2.size === 0) && (
          <div style={{ marginTop: '14px', fontSize: '12.5px', color: 'var(--text-muted)' }}>
            {current.level1.size === 0 && current.level2.size === 0
              ? 'Both levels are empty — no payouts can be approved until someone is assigned to each.'
              : current.level1.size === 0
                ? 'Level 1 is empty — payouts will sit in “pending” with nobody able to review them.'
                : 'Level 2 is empty — reviewed payouts will have nobody to give final approval.'}
          </div>
        )}

        {/* Save bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '22px', flexWrap: 'wrap' }}>
          <button onClick={handleSave} disabled={!isDirty || saving} className="btn btn-primary">
            {saving ? 'Saving…' : 'Save approvers'}
          </button>
          {isDirty && !saving && (
            <button onClick={discard} className="btn btn-ghost">Discard changes</button>
          )}
          {msg && (
            <span style={{ fontSize: '13px', color: msg.tone === 'ok' ? 'var(--green)' : 'var(--red)' }}>
              {msg.text}
            </span>
          )}
          {!isDirty && !msg && approvers && (
            <span style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>
              Signed in as {user?.display_name}
              {approvers.me.level1 || approvers.me.level2
                ? ` — you are ${approvers.me.level1 && approvers.me.level2 ? 'a Level 1 reviewer and a final approver' : approvers.me.level1 ? 'a Level 1 reviewer' : 'a final approver'}.`
                : ' — you are not currently an approver.'}
            </span>
          )}
        </div>
      </div>

      <EmailNotificationsCard />
    </div>
  );
}

const ROLE_LABEL = { rep: 'Rep', manager: 'Manager', finance_admin: 'Finance Admin', global_admin: 'Global Admin' };

function ApproverColumn({ step, title, blurb, color, users, selected, onToggle }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px', background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{
            width: '22px', height: '22px', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: color, color: 'white', fontSize: '12px', fontWeight: 700, fontFamily: 'var(--font-data)',
          }}>{step}</span>
          <strong style={{ fontSize: '14px', color: 'var(--text-primary)' }}>{title}</strong>
          <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'var(--font-data)' }}>
            {selected.size} selected
          </span>
        </div>
        <div style={{ fontSize: '12.5px', color: 'var(--text-muted)', marginTop: '6px', lineHeight: 1.5 }}>{blurb}</div>
      </div>
      <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
        {users.map(u => {
          const on = selected.has(u.id);
          const financeOk = FINANCE_ROLES.has(u.app_role);
          return (
            <label key={u.id} style={{
              display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', cursor: 'pointer',
              background: on ? 'var(--blue-subtle)' : 'transparent',
              borderBottom: '1px solid var(--border)',
            }}>
              <input
                type="checkbox"
                checked={on}
                onChange={() => onToggle(u.id)}
                style={{ width: '15px', height: '15px', accentColor: color, cursor: 'pointer' }}
              />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: '13.5px', color: 'var(--text-primary)', fontWeight: on ? 600 : 400 }}>
                  {u.display_name || u.email || `User #${u.id}`}
                </span>
                <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {u.email || '—'}
                </span>
              </span>
              <span style={{
                fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap',
                color: financeOk ? 'var(--text-secondary)' : 'var(--amber)',
              }}>
                {ROLE_LABEL[u.app_role] || u.app_role}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

// ─── Email notifications card (Settings tab) ──────────────────────────────────
// Shows whether the app can send mail and lets a global admin send themselves a
// test. Configuration itself lives in App Service settings (MAIL_FROM) and the
// Entra app registration (Mail.Send) — this card just reports on it.
function EmailNotificationsCard() {
  const { data: st, loading, refetch } = useApi(getNotificationStatus);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null);   // { tone: 'ok'|'err', text }

  const handleTest = async () => {
    setSending(true); setMsg(null);
    try {
      const r = await sendTestNotification();
      setMsg({ tone: 'ok', text: `Test sent from ${r.from} to ${(r.to || []).join(', ')}. Check your inbox (and junk, the first time).` });
      refetch();
    } catch (err) {
      setMsg({ tone: 'err', text: err.message });
    } finally {
      setSending(false);
    }
  };

  const ok = !!st?.configured;

  return (
    <div className="card" style={{ padding: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 380px' }}>
          <h3 style={{ marginBottom: '8px' }}>Email Notifications</h3>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0, lineHeight: 1.6 }}>
            Emails go out <strong style={{ color: 'var(--text-primary)' }}>from the approver's own mailbox</strong>, with the full
            HW/SW / Services / FOD detail of each payout. After a Level 1 review, final approvers get one digest from the reviewer;
            after final approval, Level 1 reviewers get one digest from the approver confirming what's approved and when it pays.
            {st?.digestSeconds ? ` Digests go ${Math.round(st.digestSeconds / 60) || 1} min after the last action, so a batch is one email.` : ''}{' '}
            A send-back emails the reviewer right away.
          </p>
        </div>
        <div style={{
          padding: '10px 14px', borderRadius: 'var(--radius-sm)', minWidth: '220px',
          background: loading ? 'var(--bg-elevated)' : ok ? 'var(--green-subtle)' : 'var(--amber-subtle)',
          border: `1px solid ${loading ? 'var(--border)' : ok ? 'var(--green)' : 'var(--amber)'}`,
        }}>
          <div style={{ fontSize: '11px', letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700, color: loading ? 'var(--text-muted)' : ok ? 'var(--green)' : 'var(--amber)' }}>
            {loading ? 'Checking…' : ok ? 'Configured' : 'Not configured'}
          </div>
          <div style={{ fontSize: '12.5px', color: 'var(--text-secondary)', marginTop: '4px', fontFamily: 'var(--font-data)' }}>
            {loading ? '' : ok ? (st.fallbackFrom ? `sends as approver · fallback ${st.fallbackFrom}` : 'sends as each approver') : `missing ${st?.missing?.join(', ') || 'Azure credentials'}`}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '18px', flexWrap: 'wrap' }}>
        <button onClick={handleTest} disabled={sending || loading} className="btn btn-primary">
          {sending ? 'Sending…' : 'Send me a test email'}
        </button>
        {msg && (
          <span style={{ fontSize: '13px', color: msg.tone === 'ok' ? 'var(--green)' : 'var(--red)', lineHeight: 1.5 }}>
            {msg.text}
          </span>
        )}
        {!msg && ok && !loading && (
          <span style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>
            Sends a sample from your mailbox to your mailbox — the same path a real approval email takes. If Graph rejects it,
            the message here says why (usually <code>Mail.Send</code> not consented yet, or the Exchange access policy excludes you).
          </span>
        )}
        {!msg && !ok && !loading && (
          <span style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>
            The app needs its Azure credentials (the same ones SSO uses) plus Graph <code>Mail.Send</code> (application) with admin
            consent on the "Weaver Commissions" registration. The test button reports exactly what Graph says if something is still missing.
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Quotas Panel ───────────────────────────────────────────────────────────────
function QuotasPanel() {
  const [quarter, setQuarterState] = useState(getCurrentQuarter());
  const { data, loading, refetch } = useApi(() => getQuotas(quarter), [quarter]);
  const [edits, setEdits]   = useState({});   // overlay of unsaved changes
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');

  // Switching quarter discards any unsaved edits and clears the status message.
  const setQuarter = (q) => { setQuarterState(q); setEdits({}); setMsg(''); };

  const quotas   = data || {};
  const teamVal  = edits.teamViewQuotaGp      ?? (quotas.teamViewQuotaGp ?? '');
  const mgrVal   = edits.managerPayoutQuotaGp ?? (quotas.managerPayoutQuotaGp ?? '');
  const isDirty  = Object.keys(edits).length > 0;

  const setField = (field, value) => setEdits(e => ({ ...e, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      await updateQuotas({ quarter, teamViewQuotaGp: teamVal, managerPayoutQuotaGp: mgrVal });
      setEdits({});
      await refetch();
      setMsg(`Saved. Team View and the manager dashboard now use these targets for ${quarter}. Other quarters are unchanged.`);
    } catch (err) {
      setMsg(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '640px' }}>
      <div className="card" style={{ padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '8px' }}>
          <div>
            <h3 style={{ marginBottom: '8px' }}>Quarterly Quotas</h3>
            <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0 }}>
              Set total-GP targets per quarter. Quarters left unset use the {fmt(1800000)} default —
              saving one quarter never changes another.
            </p>
          </div>
          <select
            value={quarter}
            onChange={e => setQuarter(e.target.value)}
            className="input"
            style={{ width: 'auto', minWidth: '130px' }}
          >
            {generateQuarterOptions().map(q => <option key={q} value={q}>{q}</option>)}
          </select>
        </div>

        {loading ? (
          <div style={{ padding: '32px', textAlign: 'center' }}><div className="loader-ring" style={{ margin: '0 auto' }} /></div>
        ) : (
          <>
            {quotas.isDefault && (
              <div style={{
                margin: '16px 0 4px', padding: '10px 14px',
                background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)', fontSize: '12px', color: 'var(--text-muted)',
              }}>
                No custom quota saved for {quarter} — using the {fmt(1800000)} default. Save below to override just this quarter.
              </div>
            )}

            <div style={{ marginTop: '20px' }}>
              <QuotaField
                label="Team View Quota (GP)"
                hint='The "call to the business" target shown on the Team View progress bar (Manager + Finance).'
                value={teamVal}
                preview={quotas.teamViewQuotaGp}
                onChange={v => setField('teamViewQuotaGp', v)}
              />
              <QuotaField
                label="Manager Payout Quota (GP)"
                hint="The quota the manager's My Dashboard commission is measured against (attainment %)."
                value={mgrVal}
                preview={quotas.managerPayoutQuotaGp}
                onChange={v => setField('managerPayoutQuotaGp', v)}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '8px' }}>
              <button onClick={handleSave} disabled={saving || !isDirty} className="btn btn-primary">
                {saving ? 'Saving...' : `Save Quotas for ${quarter}`}
              </button>
              {isDirty && !saving && (
                <button onClick={() => setEdits({})} className="btn btn-ghost btn-sm">Reset</button>
              )}
            </div>

            {msg && (
              <div style={{
                marginTop: '16px', padding: '12px 16px',
                background: msg.startsWith('Error') ? 'var(--red-subtle)' : 'var(--blue-subtle)',
                border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                fontSize: '13px', color: msg.startsWith('Error') ? 'var(--red)' : 'var(--text-accent)',
              }}>
                {msg}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function QuotaField({ label, hint, value, preview, onChange }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <div className="label" style={{ marginBottom: '6px' }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ position: 'relative', width: '220px' }}>
          <span style={{
            position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
            color: 'var(--text-muted)', fontSize: '14px', fontFamily: 'var(--font-data)',
          }}>$</span>
          <input
            type="number"
            min="0"
            step="10000"
            value={value}
            onChange={e => onChange(e.target.value)}
            className="input"
            style={{ paddingLeft: '24px', fontFamily: 'var(--font-data)' }}
          />
        </div>
        <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
          Current: {preview == null ? '—' : fmt(preview)}
        </span>
      </div>
      <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px', lineHeight: 1.4 }}>{hint}</p>
    </div>
  );
}